import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AwsUtils } from '@app/core/utils/aws-utils';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CustomValidators } from '@app/core/utils/custom-validators';
import { GuidUtils } from '@app/core/utils/guid-utils';
import { MediaService } from '@app/core/services/media.service';
import { MediaAsset } from '@app/core/models/media';
import { Direction, Ordering } from '@app/core/utils/ordering';
import { ScrollConstants } from '@app/core/utils/scroll-constants';
import { SearchCriteria } from '@app/core/utils/search-criteria';
import { SessionService } from '@app/security/session.service';
import { VideoUtils } from '@app/core/utils/video-utils';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { UploadComponent } from '@app/shared/upload/upload.component';
import { FeatureService } from '@app/core/services/feature.service';
import { VideoThumbnailService } from '@app/core/services/video-thumbnail.service';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { VideoComponent } from '../../shared/video/video.component';
import { UploadComponent as UploadComponent_1 } from '../../shared/upload/upload.component';
import { FormInputStateControlComponent } from '../../shared/form-input-state-control/form-input-state-control.component';
import { ColorPickerComponent } from '../../shared/color-picker/color-picker.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../shared/message-dialog/message-dialog.component';



@Component({
  selector: 'add-media-asset',
  templateUrl: './add-media-asset.component.html',
  styleUrls: ['./add-media-asset.component.scss'],
  standalone: true,
  imports: [FormsModule, NgClass, ReactiveFormsModule, ErrorDisplayComponent, InfiniteScrollDirective, VideoComponent, UploadComponent_1, FormInputStateControlComponent, ColorPickerComponent, MessageDialogComponent_1]
})
export class AddMediaAssetComponent implements OnInit {
  @Input() clientId: string;
  @Input() assetTypes: string[];
  @Input() maxFileSize = 0;
  @Input() minWidth;
  @Input() minHeight;
  @Input() canSelect = false;
  @Input() assetToUpdate;
  @Input() createBrand: boolean;
  @Input() editBrand: boolean;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<MediaAsset>();
  @Output() onSelect = new EventEmitter<MediaAsset>();
  @ViewChild(UploadComponent, { static: true }) uploader: UploadComponent;
  @ViewChild(MessageDialogComponent, { static: true }) messageDialog: MessageDialogComponent;
  @ViewChild('selector-area') mediaScrollArea: any;
  displayName = '';
  assetId = '';
  brandingColor: string;
  altText: string;
  videoUrl: string;
  mediaAssetGroup: UntypedFormGroup;
  imagePreviewOn = false;
  videoPreviewOn = false;
  selectionOn = false;
  loadedFile: File;
  previewImage: any;
  loadedImageValid = false;
  loadedImageWidth: number | undefined;
  loadedImageHeight: number | undefined;
  uploadStarted = false;
  uploadComplete = false;
  uploadedMediaAsset: MediaAsset;
  // Set while a new asset's file is uploading to S3; finalized in saveComplete once the upload finishes
  private pendingFinalizeKey: string | null = null;
  offset = 0;
  selectableMediaAssets: MediaAsset[] = [];
  selectedAsset: MediaAsset;
  selectionPreview = false;
  videoUtils = VideoUtils;
  searchText: string;
  searchCriteria: SearchCriteria;
  ordering = new Ordering('updated_at', Direction.Desc);

  readonly limit = 20;
  readonly throttle = ScrollConstants.throttle;
  readonly scrollDistance = ScrollConstants.scrollDistance;

  // Single source of truth for which image files are supported - drives the
  // file input's `accept` attribute (see acceptAttribute) and the
  // requirements hint (see requirementsHint) shown to the user, so those
  // can't drift out of sync with each other the way they previously did.
  readonly allowedImageExtensions = [
    'jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi', 'gif', 'png', 'svg', 'bmp', 'ico',
  ];

  // Some of the extensions above (.jpe/.jif/.jfif/.jfi in particular) aren't
  // registered in every OS's MIME database, so the browser can report an
  // empty or unexpected file.type for them even though they're genuinely
  // JPEGs. loadImage() therefore falls back to allowedImageExtensions when
  // file.type isn't recognized here, rather than rejecting a file the UI
  // just told the user was supported.
  readonly allowedImageMimeTypes = [
    'image/jpeg',
    'image/gif',
    'image/png',
    'image/svg+xml',
    'image/bmp',
    'image/x-icon',
    'image/vnd.microsoft.icon'
  ];

  get acceptAttribute(): string {
    return this.allowedImageExtensions.map(ext => `.${ext}`).join(',');
  }

  constructor(private mediaService: MediaService,
    private sessionService: SessionService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private videoThumbnailService: VideoThumbnailService,
    public featureService: FeatureService) {
  }

  ngOnInit() {
    if (this.assetToUpdate) {
      this.mediaAssetGroup = this.formBuilder.group({
        'selected_asset_type': [this.assetToUpdate.asset_type, Validators.required],
        'alternate_text': [this.assetToUpdate.alternate_text, Validators.required],
        'short_name': [this.assetToUpdate.shortname, Validators.required],
        'user_asset_id': [this.assetToUpdate.asset_id, CustomValidators.customMediaAssetIdValidator],
        'branding_color': [this.assetToUpdate.branding_color, this.editBrand ? Validators.required : []],
        'video_url': [this.assetToUpdate.url, [Validators.required, CustomValidators.whiteListURL]]
      });

      // if editing an existing id, validation isn't needed because
      // the asset_id field is read-only. Also some older media assets
      this.mediaAssetGroup.get('user_asset_id').disable();

      this.assetTypes = [this.assetToUpdate.asset_type];
      this.selectedAssetType = this.assetTypes[0];
      this.assetId = this.assetToUpdate.asset_id;
      this.displayName = this.assetToUpdate.shortname;
      this.altText = this.assetToUpdate.alternate_text;
      this.brandingColor = this.assetToUpdate.branding_color;

    } else if (this.createBrand) {
      this.mediaAssetGroup = this.formBuilder.group({
        'selected_asset_type': ['image', Validators.required],
        'alternate_text': [null, Validators.required],
        'short_name': [null, Validators.required],
        'user_asset_id': [null, CustomValidators.customMediaAssetIdValidator],
        'branding_color': ['#000000', Validators.required],
        'video_url': [null, [Validators.required, CustomValidators.whiteListURL]]
      });
    } else {
      this.mediaAssetGroup = this.formBuilder.group({
        'selected_asset_type': ['image', Validators.required],
        'alternate_text': [null, Validators.required],
        'short_name': [null, Validators.required],
        'user_asset_id': [null, CustomValidators.customMediaAssetIdValidator],
        'video_url': [null, [Validators.required, CustomValidators.whiteListURL]]
      });
    }

    this.mediaAssetGroup.controls['video_url'].disable();
    this.mediaAssetGroup.get('selected_asset_type').valueChanges.subscribe((assetType: string) => {
      if (assetType === 'image' || assetType === 'banner') {
        this.mediaAssetGroup.get('alternate_text').setValidators([Validators.required]);
        this.mediaAssetGroup.get('alternate_text').enable();
      } else {
        this.mediaAssetGroup.get('alternate_text').setValidators([]);
        this.mediaAssetGroup.get('alternate_text').disable();
      }
    });

    if (this.assetTypes.length === 1) {
      this.selectedAssetType = this.assetTypes[0];
    }

    this.mediaAssetGroup.valueChanges.subscribe(val => {
      this.altText = val['alternate_text'];
      this.displayName = val['short_name'];
      this.assetId = val['user_asset_id'];
      this.videoUrl = val['video_url'];
      this.brandingColor = val['branding_color'];
    });
  }

  get selectedAssetType(): string {
    return this.mediaAssetGroup.get('selected_asset_type').value;
  }

  set selectedAssetType(newType: string) {
    this.mediaAssetGroup.get('selected_asset_type').setValue(newType);
  }

  get isValid() {
    if (this.mediaAssetGroup.valid && this.loadedImageValid
      || this.selectedAssetType === 'video' && this.mediaAssetGroup.valid
      || this.selectedAsset) {
      return true;
    } else {
      return false;
    }
  }

  get currentVideoUrl() {
    if (this.selectedAsset) {
      return this.selectedAsset.url;
    }
    return this.videoUrl || '';
  }

  get requirementsHint(): string {
    const supportedFormats = this.allowedImageExtensions.map(ext => ext.toUpperCase()).join(', ');
    const formatsAndSize = `${supportedFormats}\n· Max file size ${this.formattedMaxFileSize}`;
    if (this.selectedAssetType === 'brand') {
      return `${formatsAndSize}\n· Must be square (equal width & height)`;
    } else if (this.selectedAssetType === 'banner') {
      return `${formatsAndSize}\n· Min width ${this.effectiveMinWidth} pixels`;
    }
    return formatsAndSize;
  }

  private get formattedMaxFileSize(): string {
    if (!this.maxFileSize) {
      return '1MB';
    }
    const megabytes = Math.round((this.maxFileSize / (1024 * 1024)) * 10) / 10;
    return `${megabytes}MB`;
  }

  get uploadedFileName(): string {
    return this.loadedFile ? this.loadedFile.name : '';
  }

  get uploadedFileTypeLabel(): string {
    if (!this.loadedFile) {
      return '';
    }
    const extension = this.loadedFile.name.split('.').pop();
    return extension ? `${extension.toUpperCase()} File` : '';
  }

  get uploadedImageDimensions(): string {
    if (!this.loadedImageWidth || !this.loadedImageHeight) {
      return '';
    }
    return `${this.loadedImageWidth}x${this.loadedImageHeight}`;
  }

  get uploadedFileSizeLabel(): string {
    if (!this.loadedFile) {
      return '';
    }
    const kilobytes = Math.round(this.loadedFile.size / 1024);
    return `${kilobytes} KB`;
  }

  setColor(event: string | boolean) {
    if (this.createBrand || this.editBrand) {
      this.mediaAssetGroup.get('branding_color').setValue(event);
    }
  }

  clearSearch() {
    this.searchText = undefined;
    this.searchCriteria = undefined;
    this.selectedAsset = undefined;
    this.displayName = undefined;
    this.assetId = undefined;
    this.resetSelectableMediaAssets();
  }

  checkForEnter(event) {
    if (event.keyCode === 13) {
      this.submitSearchRequest();
    }
  }

  confirmSelection() {
    this.onSelect.emit(this.selectedAsset);
  }

  close() {
    this.onClose.emit();
  }

  dragOver(event) {
    event.preventDefault();
  }

  dropFile(event) {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.loadImage(file);
    }
  }

  getSelectableMediaAssets() {
    let mediaAssetObs: Observable<Array<MediaAsset>>;

    mediaAssetObs = this.mediaService.getMediaAssetsByClient(this.clientId, this.selectedAssetType, this.limit,
      this.offset, this.ordering, this.searchCriteria, this.offset !== 0);

    mediaAssetObs.subscribe(
      mediaAssets => {
        this.selectableMediaAssets = this.selectableMediaAssets.concat(mediaAssets);
        this.videoThumbnailService.setVideoThumbnailUrls(this.selectableMediaAssets);
      },
      error => {
        if (error.status !== 401) {
          this.messageDialog.showMessage('Oops...there was an error getting media assets.');
        }
      });
  }

  handleUploadError(event: Object) {
    // S3 (presigned upload) error responses are XML, not JSON, unlike rn-portal's
    let errorMessage: string;
    try {
      errorMessage = JSON.parse(event['response'])['response'];
    } catch {
      errorMessage = undefined;
    }
    if (!errorMessage || errorMessage === '') {
      errorMessage = 'Oops... there was an error uploading the file. Please try re-exporting the file.';
    }
    this.messageDialog.showMessage(errorMessage);
    this.resetUploadState();
  }

  /**
   * Some extensions we advertise as supported (.jpe/.jif/.jfif/.jfi) aren't
   * registered in every OS's MIME database, so file.type can come back empty
   * or unrecognized for a genuinely valid file. Fall back to the file's
   * extension in that case rather than rejecting a format the UI just told
   * the user was supported.
   */
  private isAllowedImageFile(file: File): boolean {
    if (this.allowedImageMimeTypes.indexOf(file.type) !== -1) {
      return true;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    return !!extension && this.allowedImageExtensions.indexOf(extension) !== -1;
  }

  loadImage(file: File) {
    if (file.size > this.maxFileSize) {
      this.messageDialog.showMessage('File is too big!  All images must be less than 1 MB.');
    } else if (!this.isAllowedImageFile(file)) {
      this.messageDialog.showMessage('That file type is not allowed.  Please select an image file.');
    } else {

      const fileReader = new FileReader();

      fileReader.onload = (event) => {

        const imageElement = <HTMLImageElement>document.getElementById('previewImage');
        this.previewImage = fileReader.result;
        // { once: true } is required because #previewImage is a single persistent
        // element reused across every select/remove/reselect cycle in this dialog -
        // confirmed via instrumentation that repeated selections were stacking a new
        // 'load' listener on the same node each time without this.
        imageElement.addEventListener('load', (e) => {
          if (imageElement.naturalWidth < this.effectiveMinWidth) {
            const error = `Oops...that image has a width of ${imageElement.naturalWidth} pixels, but must be at least ${this.effectiveMinWidth} pixels wide.`;
            this.messageDialog.showMessage(error);
          } else if (imageElement.naturalHeight < this.effectiveMinHeight) {
            const error = `Oops...that image has a height of ${imageElement.naturalHeight} pixels, but must be at least ${this.effectiveMinHeight} pixels high.`;
            this.messageDialog.showMessage(error);
          } else if (this.selectedAssetType === 'brand' && imageElement.naturalWidth !== imageElement.naturalHeight) {
            const error = `Oops...that image is not square and brand icons have to be square`;
            this.messageDialog.showMessage(error);
          } else {
            this.loadedImageValid = true;
            this.loadedImageWidth = imageElement.naturalWidth;
            this.loadedImageHeight = imageElement.naturalHeight;
          }
        }, { once: true });
        this.loadedFile = file;
        this.imagePreviewOn = true;
        this.selectionOn = false;
      };
      fileReader.readAsDataURL(file);
    }
  }

  nextStepForVideo() {
    this.videoPreviewOn = true;
  }

  onAssetTypeChanged() {
    if (this.searchCriteria) {
      this.searchCriteria.clearFilters();
      this.searchCriteria.addFilter('asset_type', this.selectedAssetType);
    }

    this.clearLoadedImage();
    this.resetSelectableMediaAssets();
    this.manageVideoFormControls();
  }

  onVideoUrlFocus() {
    if (this.selectedAsset) {
      this.selectedAsset = undefined;
      this.displayName = undefined;
      this.assetId = undefined;
      this.altText = undefined;
    }
  }

  onVideoUrlBlur(): void {
    this.mediaAssetGroup.controls['video_url'].setValue(VideoUtils.forceHttps(this.mediaAssetGroup.controls['video_url'].value));
    this.mediaAssetGroup.controls['video_url'].updateValueAndValidity();
  }

  onScrollDown() {
    this.offset += this.limit;
    this.getSelectableMediaAssets();
  }

  previewSelection() {
    this.selectionPreview = true;
    this.selectionOn = false;
    if (this.selectedAsset.asset_type === 'video') {
      this.videoPreviewOn = true;
      this.videoUrl = this.selectedAsset.url;
    } else {
      const imageElement = <HTMLImageElement>document.getElementById('previewImage');
      imageElement.src = this.selectedAsset.s3_url;
      this.imagePreviewOn = true;
    }
  }

  /**
   * Resets the dialog back to the initial drag-drop picker so the user can
   * select a different file, without closing the dialog. Used for the
   * error-state "Retry" action, and for the "remove" action available on a
   * loaded (but not yet uploaded) preview.
   */
  resetState() {
    this.loadedFile = undefined;
    this.previewImage = undefined;
    this.loadedImageValid = false;
    this.loadedImageWidth = undefined;
    this.loadedImageHeight = undefined;
    this.imagePreviewOn = false;
    this.selectionOn = false;
    this.selectedAsset = undefined;
    this.selectionPreview = false;
    this.uploadStarted = false;
    this.uploadComplete = false;
    this.resetAssetIdentityFields();
    this.pendingFinalizeKey = null;
    this.changeDetectorRef.detectChanges();
  }

  save() {
    // Guards against a second save request racing the presigned-upload flow and overwriting pendingFinalizeKey
    if (this.uploadStarted) {
      return;
    }
    if (this.isValid) {
      if (this.selectedAssetType === 'video') {
        this.saveVideo();
      } else {
        this.saveImageOrBrand();
      }
    }
  }

  saveAborted() {
    this.uploadStarted = false;
    this.pendingFinalizeKey = null;
  }

  saveComplete(uploadObj: Object) {
    if (this.pendingFinalizeKey) {
      this.finalizeNewMediaAsset(this.pendingFinalizeKey);
      return;
    }
    this.uploadComplete = true;
    // todo - this is put in to be safe. caused by a backend bug: https://rn-jira.atlassian.net/browse/DV-2884
    const resp = uploadObj['response'];
    const url = resp ? AwsUtils.addHostName(resp['s3_url']) : 'undefined';
    this.uploadedMediaAsset.s3_url = url;
    this.uploadedMediaAsset.updated_at = new Date(); // For cache busting purposes
    // Unlike saveVideo(), this path no longer needs the update-then-fetch
    // hydration for created_by_name/updated_by_name: finalizeNewMediaAsset's
    // dedicated create endpoint already resolves those search-index fields
    // server-side. The remaining case this branch covers (updating an
    // existing asset's file, via the legacy non-presigned upload) can still
    // leave them stale - tracked separately as a follow-up bug ticket rather
    // than re-adding the extra PUT+GET round trip here.
    this.onSave.emit(this.uploadedMediaAsset);
  }

  private finalizeNewMediaAsset(key: string) {
    this.pendingFinalizeKey = null;
    this.mediaService.finalizeMediaAsset(this.clientId, {
      assetType: this.uploadedMediaAsset.asset_type,
      assetId: this.uploadedMediaAsset.asset_id,
      key,
      fileSize: this.uploadedMediaAsset.file_size,
      shortname: this.uploadedMediaAsset.shortname,
      description: this.uploadedMediaAsset.description ?? null,
      alternateText: this.uploadedMediaAsset.alternate_text ?? null,
      tags: this.uploadedMediaAsset.tags ?? [],
      ext: {},
      attributes: (this.uploadedMediaAsset.attributes as Record<string, unknown>) ?? {},
      isDisabled: this.uploadedMediaAsset.is_disabled ?? false,
    }).subscribe({
      next: () => {
        this.uploadComplete = true;
        this.uploadedMediaAsset.s3_url = AwsUtils.addHostName(`rn-public/${key}`);
        this.uploadedMediaAsset.updated_at = new Date();
        this.onSave.emit(this.uploadedMediaAsset);
      },
      error: (error) => {
        const message = error?.error?.reason ?? error?.error?.message;
        this.messageDialog.showMessage(message || 'Oops...the media asset could not be saved.');
        this.resetUploadState();
      },
    });
  }

  private resetUploadState() {
    this.loadedFile = undefined;
    this.uploadStarted = false;
    this.uploadComplete = false;
    this.imagePreviewOn = false;
    this.pendingFinalizeKey = null;
    if (this.canSelect) {
      this.turnOnSelection();
    }
    this.changeDetectorRef.detectChanges();
  }

  selectExistingAsset(selectedAsset: MediaAsset) {
    this.selectedAsset = selectedAsset;
    this.mediaAssetGroup.get('selected_asset_type').setValue(selectedAsset.asset_type);
    this.mediaAssetGroup.get('alternate_text').setValue(selectedAsset.alternate_text);
    this.mediaAssetGroup.get('short_name').setValue(selectedAsset.shortname);
    this.mediaAssetGroup.get('user_asset_id').setValue(selectedAsset.asset_id);
    this.mediaAssetGroup.get('video_url').setValue(selectedAsset.url);
    this.mediaAssetGroup.get('branding_color').setValue(selectedAsset.branding_color);
    CustomValidators.markAllAsUntouched(this.mediaAssetGroup);
  }

  selectLocalFile(event) {
    if (event.target['files'].length > 0) {
      const file = event.target['files'][0];
      this.loadImage(file);
      event.target.value = '';

      // Clear the display name and asset ID of an existing asset that was selected.
      // But don't clear them if the user entered them.
      if (this.selectionOn && this.selectedAsset) {
        this.displayName = undefined;
        this.assetId = undefined;
        this.altText = undefined;
      }
      this.selectedAsset = undefined;
      this.changeDetectorRef.detectChanges();
    }
  }

  submitSearchRequest() {
    if (this.searchText) {
      this.selectedAsset = undefined;
      this.searchCriteria = new SearchCriteria();
      this.searchCriteria.searchPhrase = this.searchText;
      this.searchCriteria.searchFields = ['shortname', 'asset_id', 'description', 'created_by'];
      this.searchCriteria.addFilter('asset_type', this.selectedAssetType);
      this.resetSelectableMediaAssets();
    }
  }

  turnOnSelection() {
    this.selectionOn = true;
    this.loadedFile = undefined;
    this.loadedImageValid = false;
    this.imagePreviewOn = false;
    this.getSelectableMediaAssets();
  }

  private clearLoadedImage() {
    this.clearLoadedImagePreview();
    this.resetAssetIdentityFields();
  }

  private clearLoadedImagePreview() {
    this.loadedFile = undefined;
    this.previewImage = undefined;
    this.loadedImageValid = false;
    this.loadedImageWidth = undefined;
    this.loadedImageHeight = undefined;
    this.imagePreviewOn = false;
    this.changeDetectorRef.detectChanges();
  }

  private resetAssetIdentityFields() {
    ['short_name', 'alternate_text', 'user_asset_id', 'video_url'].forEach(controlName => {
      this.mediaAssetGroup.get(controlName)?.reset();
    });
  }

  private resetSelectableMediaAssets() {
    if (this.canSelect && this.selectionOn) {
      this.selectableMediaAssets = [];
      this.offset = 0;
      this.getSelectableMediaAssets();
    }
  }

  private manageVideoFormControls() {
    if (this.selectedAssetType === 'video') {
      this.mediaAssetGroup.controls['video_url'].enable();
    } else {
      this.mediaAssetGroup.controls['video_url'].disable();
    }
  }

  private get effectiveMinWidth() {
    let effectiveMinWidth = 0;
    if (this.minWidth) {
      effectiveMinWidth = this.minWidth;
    } else if (this.selectedAssetType === 'brand') {
      effectiveMinWidth = 95;
    } else if (this.selectedAssetType === 'banner') {
      effectiveMinWidth = 1200;
    }
    return effectiveMinWidth;
  }

  private get effectiveMinHeight() {
    let effectiveMinHeight = 0;
    if (this.minHeight) {
      effectiveMinHeight = this.minHeight;
    } else {
      if (this.selectedAssetType === 'brand') {
        effectiveMinHeight = 95;
      }
    }
    return effectiveMinHeight;
  }

  private ensureAssetIdDefined() {
    if (!this.assetId) {
      this.assetId = GuidUtils.createRandomGuid();
    }
  }

  private saveImageOrBrand() {
    if (this.assetToUpdate) {
      this.updateImageOrBrand();
    } else {
      this.createImageOrBrand();
    }
  }

  /** Uploads a new image/brand/banner asset directly to S3 via a presigned URL, then finalizes it in rn-portal */
  private createImageOrBrand() {
    this.ensureAssetIdDefined();

    this.uploadedMediaAsset = new MediaAsset();
    this.uploadedMediaAsset.asset_id = this.assetId;
    this.uploadedMediaAsset.asset_type = this.selectedAssetType;
    this.uploadedMediaAsset.created_by = this.sessionService.currentUser.email_address;
    this.uploadedMediaAsset.shortname = this.displayName;
    this.uploadedMediaAsset.updated_by = this.uploadedMediaAsset.created_by;
    this.uploadedMediaAsset.file_size = this.loadedFile.size.toString();
    this.uploadedMediaAsset.alternate_text = this.altText;
    this.uploadedMediaAsset.branding_color = this.brandingColor;

    this.uploadStarted = true;

    // Some files (e.g. dragged from certain sources) report an empty type; fall back so the
    // presigned URL's signed content-type still matches the header sent with the S3 PUT
    const contentType = this.loadedFile.type || 'application/octet-stream';

    this.mediaService
      .getPresignedUploadUrl(
        this.clientId,
        this.selectedAssetType,
        this.assetId,
        this.loadedFile.name,
        contentType,
      )
      .subscribe({
        next: ({ url, key }) => {
          this.pendingFinalizeKey = key;
          this.uploader.startUpload(url, this.loadedFile, null, 'PUT', {
            withCredentials: false,
            contentType,
          });
        },
        error: (error) => {
          this.uploadStarted = false;
          const message = error?.error?.reason ?? error?.error?.message;
          this.messageDialog.showMessage(message || 'Oops...there was an error preparing the upload.');
        },
      });
  }

  /** Legacy path for updating an existing asset's file; not yet migrated to presigned uploads */
  private updateImageOrBrand() {
    const url = this.mediaService.getUpdateUrl(this.clientId, this.assetToUpdate.asset_id);

    this.ensureAssetIdDefined();

    this.uploadedMediaAsset = this.assetToUpdate;
    this.uploadedMediaAsset.shortname = this.displayName;
    this.uploadedMediaAsset.updated_by = this.uploadedMediaAsset.created_by;
    this.uploadedMediaAsset.file_size = this.loadedFile.size.toString();
    this.uploadedMediaAsset.alternate_text = this.altText;
    this.uploadedMediaAsset.branding_color = this.brandingColor;

    const json = JSON.stringify(this.uploadedMediaAsset.serialize(this.assetToUpdate));

    const formData = new FormData();
    formData.append('body', json);

    this.uploader.startUpload(url, this.loadedFile, formData, 'PUT');
    this.uploadStarted = true;
  }

  private saveVideo() {
    this.ensureAssetIdDefined();
    const videoAsset = new MediaAsset();
    videoAsset.asset_id = this.assetId;
    videoAsset.asset_type = this.selectedAssetType;
    videoAsset.shortname = this.displayName;
    videoAsset.created_by = this.sessionService.currentUser.email_address;
    videoAsset.updated_by = videoAsset.created_by;
    videoAsset.url = this.videoUrl;
    this.mediaService.createMediaAsset(videoAsset).subscribe({
      next: () => {
        // Deliberate behavior change: the create endpoint alone leaves the
        // search index missing created_by_name/updated_by_name, so we follow
        // up with the same update-then-fetch sequence
        // edit-media-asset-details.saveChanges() uses (a metadata PUT, then a
        // GET to pick up the backend-resolved fields). That mirrors the
        // existing pattern rather than inventing a new one, but it does mean
        // every save now costs two extra API calls and has a larger failure
        // surface.
        //
        // The video asset above has already been created by this point, so a
        // failure in this follow-up only means its display fields may lag -
        // it doesn't mean creation failed. We still emit and mark the upload
        // complete, and let the user know, rather than showing the same
        // "could not be created" error for a create that actually succeeded.
        this.mediaService.updateMediaAssetMetaData(videoAsset).pipe(
          switchMap(() => this.mediaService.getMediaAsset(videoAsset.asset_id, false))
        ).subscribe({
          next: (savedMediaAsset) => {
            this.uploadComplete = true;
            this.onSave.emit(savedMediaAsset);
          },
          error: () => {
            this.uploadComplete = true;
            this.onSave.emit(videoAsset);
            this.messageDialog.showMessage(
              'The video was saved, but some of its details may not display correctly until the media library is refreshed.',
            );
          },
        });
      },
      error: () => {
        this.messageDialog.showMessage('Oops...the media asset could not be created.');
      },
    });
  }
}
